export default {
    title: 'Prepare Meeting Handout',
    subtitle: 'Create a professional handout to guide your evaluation meeting',
    introCard: {
        title: "First, Let's Create the Employee Handout",
        subtitle: 'It will guide your reflection to organize the meeting.',
    },
    handout: {
        title: 'Performance Discussion Summary',
        evaluationDate: 'Evaluation Date: {{date}}',
        evaluationPeriod: 'Reference Period: {{startDate}} to {{endDate}}',
        manager: '{{managerLabel}}: {{name}}',
        pageNumber: 'Page {{current}} of {{total}}',
        employeeProfile: 'Competency profile: {{profile}}',
        documentGenerated: 'Document generated {{date}}',
        selectEvaluationPeriod: 'Select reference period',
        defineCustomDates: 'Define your own start and end dates',
    },
    performance: {
        label: 'Performance',
        exceptionalImpact: 'Exceptional Impact',
        exceedsExpectations: 'Exceeds expectations',
        meetsExpectations: 'Meets expectations',
        needsDevelopment: 'Needs Development',
        unsatisfactory: 'Unsatisfactory',
        notObserved: 'Not Observed',
    },
    companyValues: {
        title: 'Alignment with Company Values',
        subtitle: 'How you embody our core values',
        mastered: 'Mastered',
        adherence: 'Adherence',
        deleteSection: 'Delete this section',
        focusOnImpact: {
            title: 'Focus on Impact',
            description:
                'When you deliver today is worth more than you imagine for tomorrow, leave breadcrumbs, test, and adjust.',
        },
        cultivatePleasure: {
            title: 'Cultivate pleasure',
            description:
                "Experiencing pleasure at work is not a luxury, it's a choice and a shared responsibility.",
        },
        awakenPotential: {
            title: 'Awaken your potential',
            description:
                'Bring out the best in yourself, your own good idea, ask questions, challenge the status quo.',
        },
        makeDifference: {
            title: 'Make a difference',
            description:
                'Bring your people to you, you see the constraints, but you also see the opportunities.',
        },
    },
    competencyGroups: {
        title: 'Competency groups',
        empty: 'No competency groups available yet.',
        unrated: 'Not rated',
        evaluationLabel: 'Evaluation',
    },
    competencies: {
        mastered: {
            title: 'Mastered Competencies',
            regenerateWithAI: 'Regenerate with AI',
            projectManagement: {
                title: 'Project Management',
                description:
                    'Demonstrates strong ability to plan, organize and execute projects effectively with clear timelines and deliverables.',
            },
            strategicPlanning: {
                title: 'Strategic Planning',
                description:
                    'Capable of contributing to long-term planning and aligning initiatives with organizational goals.',
            },
            stakeholderManagement: {
                title: 'Stakeholder Management',
                description:
                    'Excels at building and maintaining positive relationships with all stakeholders, ensuring clear communication and alignment.',
            },
        },
        growth: {
            title: 'Areas of Excellence',
            regenerateWithAI: 'Regenerate with AI',
            javascript: {
                title: 'JavaScript/TypeScript',
                description:
                    'Proficient in modern JavaScript and TypeScript, writing clean, maintainable code for various applications.',
            },
            dataAnalysis: {
                title: 'Data Analysis',
                description:
                    'Developing competencies in analyzing and interpreting data for business insights.',
            },
            publicSpeaking: {
                title: 'Public Speaking',
                description: 'Developing competency in presenting and speaking to audiences.',
            },
        },
        addSkill: 'Add competency',
        add: 'Add',
        cancel: 'Cancel',
        save: 'Save',
        generating: 'Generating...',
        regenerateWithAI: 'Regenerate with AI',
        replaceListed: 'Replace listed competencies',
        replaceListedHint: 'Select 3 key competencies and replace the current list.',
        replaceListedExhausted: 'No alternative competencies available to replace.',
        addThree: 'Add 3 key competencies',
        addThreeHint: 'Add the most representative competencies from the evaluation.',
        addThreeExhausted: 'No more competencies available to add.',
        defaultDescription: 'Add a short description for this competency.',
        skillTitlePlaceholder: 'Competency title',
        skillDescriptionPlaceholder: 'Competency description',
        customGroup: 'Custom',
        selectCompetency: 'Select a Competency',
        chooseCompetency: 'Choose a competency to add to this section',
        sortBy: 'Sort by',
        name: 'Name',
        date: 'Date',
        added: 'Added',
        insert: 'Insert',
        proficiency: 'Proficiency',
        proficiencyLevels: 'Proficiency Levels',
        close: 'Close',
    },
    recommendations: {
        title: 'Recommendations for your development',
        regenerateWithAI: 'Regenerate with AI',
        placeholder: 'Enter development recommendations...',
        delete: 'Delete',
        save: 'Save',
    },
    notes: {
        addNote: 'Add note',
        addSection: 'Add Section',
        sectionTitle: 'Section title',
        sectionPlaceholder: 'Describe the focus for this section...',
        deleteSection: 'Delete',
        notePlaceholder: 'Write a note for the meeting...',
        deleteNote: 'Delete note',
        presetSections: {
            focus: { title: 'What you should focus on...' },
            excelled: { title: 'You excelled in...' },
            nextStep: { title: 'Your next step for your career progression' },
            recommendations: { title: 'Recommendations for your development' },
        },
        saveSection: 'Save',
        editSection: 'Edit',
    },
    download: {
        title: 'Download your employee handout',
        subtitle: 'It will serve as a guide during your conversation',
        button: 'Download Handout',
        fileNameWithDate: 'Evaluation of {{name}} - {{date}}.pdf',
        fileNameWithoutDate: 'Evaluation of {{name}}.pdf',
        defaultFileName: 'Performance_Discussion_Summary.pdf',
        success: 'Your handout has been downloaded.',
        error: 'The handout could not be downloaded. Try again in a moment.',
    },
    readyToMeet: {
        title: 'Ready to meet with',
        subtitle:
            "You're all set! Start your meeting using the preparation you have done and the insights you have gathered.",
        button: 'Meet {{firstName}}',
    },
    proficiencyLegend: {
        title: 'Proficiency level legend',
    },
    stepper: {
        evaluation: 'Evaluation',
        results: 'Results',
        prepareMeeting: 'Prepare Meeting',
        meet: 'Meet',
    },
    aiRecommendations: {
        title: 'Generate recommendations with Amélio AI',
        step1: {
            question: "What is your overall feeling about the employee's performance?",
            placeholder:
                'Describe your feeling...\nFor example: The employee showed excellent commitment, but the quality of some deliverables was below expectations.',
        },
        step2: {
            question: 'What would you like the employee to develop as a priority?',
            placeholder: 'Add a brief description of the priority to develop...',
            generateButton: 'Generate recommendations',
        },
        integrate: {
            title: 'How would you like to integrate the recommendations?',
            addTitle: 'Add to existing text',
            addDescription: 'Keep the current text and append the AI recommendations',
            replaceTitle: 'Replace text',
            replaceDescription: 'Delete the current text and use only the AI recommendations',
        },
        overlay: {
            generating: 'Generating',
            message: 'AI is analyzing data and generating your personalized recommendations',
        },
        imageAlt: 'AI-powered meeting recommendation illustration',
    },
    loading: 'Loading...',
    errors: {
        loadFailed: 'Failed to load handout data',
        loadFailedDescription: 'Unable to retrieve the handout preview. Please try again.',
    },
};
